import { classes } from 'html-classes';

import { company } from '~/company/Company';
import type { ProductBadge } from '~/components/ProductCardV3/hooks/useProductCardV3';
import BadgeV3, {
  type BadgeV3Size,
  type BadgeV3Variant,
} from '~/components/ProductCardV3/shared/BadgeV3';

import styles from './ProductBadges.module.scss';

/**
 * Badge size (matches card size)
 */
export type BadgeSize = 'xs' | 's' | 'm';

/**
 * Props for ProductBadges shared component
 */
export interface ProductBadgesProps {
  /**
   * Badges array from useProductCardV3 hook (max 3)
   */
  badges: ProductBadge[];

  /**
   * Badge size (determines badge dimensions)
   */
  size: BadgeSize;

  /**
   * Badge visual variant (defaults to primary)
   */
  variant?: BadgeV3Variant;

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Shared component for grouping and displaying product badges
 * Renders badges array from useProductCardV3 hook with max 3 badges
 * Badge order (top to bottom): Coming Soon → Discount → Bundle → Express → Delivery Date
 */
const ProductBadges = ({
  badges,
  size,
  variant = 'primary',
  className,
}: ProductBadgesProps) => {
  const isHiddenBySize = company.match({
    mcosmetic: false,
    default: size === 'xs',
  });

  if (isHiddenBySize || badges.length === 0) {
    return null;
  }

  const badgeSize: BadgeV3Size =
    size === 'xs' ? 'xs' : size === 'm' ? 'm' : 's';

  return (
    <div
      className={classes([styles.badgesContainer, className])}
      data-company={company.name}
    >
      {badges.map((badge) => {
        if (badge.type === 'deliveryDate') {
          return (
            <BadgeV3
              variant={variant}
              key={badge.type}
              size={badgeSize}
              deliveryDate={badge.deliveryDate ?? null}
              className={styles.badge}
              badgeType={badge.type}
            />
          );
        }

        return (
          <BadgeV3
            variant={variant}
            key={
              badge.id !== undefined
                ? `catalog-${badge.id}`
                : `${badge.type}-${badge.content}`
            }
            size={badgeSize}
            className={styles.badge}
            badgeType={badge.type}
            backgroundColor={badge.backgroundColor}
            textColor={badge.textColor}
          >
            {badge.content}
          </BadgeV3>
        );
      })}
    </div>
  );
};

export default ProductBadges;
